import { Divider, FormControl, FormHelperText, List } from "@mui/material";
import _ from "lodash";
import { useEffect, useMemo, useState, type FC } from "react";
import {
  Controller,
  useFormContext,
  useWatch,
  type FieldValues,
  type UseFormSetValue,
} from "react-hook-form";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { isInnerItemEditingAtom, Log, type ICustomField } from "@ryde/shared";

import { GroupTitleTemplate } from "../group_title.template";
import { RouteScheduleItem } from "./route-schedule-item";
import { RouteScheduleItemAdd } from "./route-schedule-item.add";
import {
  emptyRouteSchedule,
  type IRouteScheduleItem,
} from "./table-route-schedule.models";
import {
  TableRouteScheduleRow,
  TableRouteScheduleStyled,
} from "./table-route-schedule.styled";

const normalizeRouteScheduleListFieldData = (
  watchedList: unknown,
  fieldName: string,
  setValue: UseFormSetValue<FieldValues>,
): IRouteScheduleItem[] => {
  if (watchedList && !_.isArray(watchedList)) {
    /* guard for invalid value coming from backend*/
    setValue(fieldName, []);
    return [];
  }

  return watchedList as IRouteScheduleItem[];
};

export const TableRouteScheduleTemplate: FC<ICustomField> = ({
  name,
  formLabel,
  formAttributes,
}) => {
  const { control, setValue } = useFormContext();
  const watchedValue = useWatch({ control, name });
  const [editingCount, setEditingCount] = useState(0);
  const setIsInnerItemEditing = useSetRecoilState(isInnerItemEditingAtom);
  const routeScheduleList = useMemo(() => {
    const routeScheduleListValue = normalizeRouteScheduleListFieldData(
      watchedValue,
      name,
      setValue,
    );
    Log.debug("Route schedule list normalized value", routeScheduleListValue);
    return routeScheduleListValue;
  }, [name, setValue, watchedValue]);

  const removeHandler = useEvent((index: number) => {
    Log.debug("Remove route schedule handler fired for index ", index);
    routeScheduleList?.splice(index, 1);
    setValue(name, routeScheduleList || [], {
      shouldDirty: true,
      shouldValidate: true,
    });
  });

  const { disabled } = formAttributes || {};

  const handleEditStart = useEvent(() => {
    setEditingCount(prev => prev + 1);
  });

  const handleEditEnd = useEvent(() => {
    setEditingCount(prev => Math.max(0, prev - 1));
  });

  useEffect(() => {
    setIsInnerItemEditing(editingCount > 0);
  }, [editingCount, setIsInnerItemEditing]);

  useEffect(() => {
    return () => {
      setIsInnerItemEditing(false);
    };
  }, [setIsInnerItemEditing]);

  return (
    <TableRouteScheduleStyled>
      <Controller
        name={name}
        control={control}
        render={({ fieldState: { error } }) => {
          return (
            <FormControl
              error={!!error}
              data-e2e-template="table-availability-schedule-template"
              className="no-blur-item"
            >
              <GroupTitleTemplate
                formLabel={formLabel}
                name={formLabel + "_label"}
                formAttributes={formAttributes}
              />
              <div
                className="route-schedule-list"
                data-inner-editing={editingCount > 0 ? "true" : undefined}
              >
                <List sx={{ width: "100%", bgcolor: "background.paper" }}>
                  {routeScheduleList?.map((routeScheduleField, index) => {
                    const listField =
                      routeScheduleField?.dayTypeIds ||
                      routeScheduleField?.dayGroupIds
                        ? routeScheduleField
                        : emptyRouteSchedule;
                    const uniqueKeyValue = `${listField.time}_${listField.direction}_${listField.dayTypeIds}_${listField.dayGroupIds}`;

                    return (
                      <TableRouteScheduleRow
                        data-e2e-value={uniqueKeyValue}
                        key={uniqueKeyValue}
                      >
                        <RouteScheduleItem
                          fieldName={name}
                          control={control}
                          index={index}
                          setValue={setValue}
                          value={listField || emptyRouteSchedule}
                          removeHandler={removeHandler}
                          disabledEdit={disabled}
                          disabledRemove={disabled}
                          onEditStart={handleEditStart}
                          onEditEnd={handleEditEnd}
                        />
                        <Divider
                          variant="inset"
                          component="li"
                          sx={{ my: "10px" }}
                        />
                      </TableRouteScheduleRow>
                    );
                  })}
                  {!disabled && (
                    <RouteScheduleItemAdd
                      fieldName={name}
                      index={routeScheduleList?.length || 0}
                      setValue={setValue}
                      onEditStart={handleEditStart}
                      onEditEnd={handleEditEnd}
                    />
                  )}
                </List>
              </div>

              {error && <FormHelperText>{error.message}</FormHelperText>}
            </FormControl>
          );
        }}
      />
    </TableRouteScheduleStyled>
  );
};
